<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { PencilIcon } from 'lucide-vue-next';
import { router, ROUTES } from '@/router.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { ProfileConfigService } from '@/services/config/ProfileConfigService.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';

const props = defineProps<{
    profileConfigId: ProfileConfig['id'];
    stepConfigId: StepConfig['id'];
    promptConfigId: PromptConfig['id'];
}>();

const navigateToEditPage = () => {
    const profileConfig = ProfileConfigService.getProfileConfig(props.profileConfigId);
    if (!profileConfig) {
        return;
    }

    if (profileConfig.editable) {
        void router.push({
            name: ROUTES.editUpdate,
            params: {
                profileConfigId: props.profileConfigId,
            },
            query: {
                stepConfigId: props.stepConfigId,
                promptConfigId: props.promptConfigId,
            },
        });
    } else {
        createTextToast(
            'You cannot edit default profiles',
            `Please duplicate the "${profileConfig.name}" profile and edit the copy instead.`,
            'warning',
            false,
        );
        void router.push({
            name: ROUTES.editIndex,
        });
    }
};
</script>

<template>
    <TooltipProvider :delay-duration="150">
        <Tooltip>
            <TooltipTrigger as-child>
                <Badge class="h-5 rounded-sm" variant="secondary" @click="navigateToEditPage">
                    <PencilIcon class="h-3.5 w-3.5" />
                </Badge>
            </TooltipTrigger>
            <TooltipContent>
                <p>Edit prompt</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
</template>
